<script lang="ts">
  import { Component, Prop, Vue } from 'vue-property-decorator';
  import { Globals } from '@/modules/Globals';
  import { vueData } from '@/VueData';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    ICategorySearchResumed,
    ICategorySearchResumedWithItems,
  } from '@/interfaces/ICategorySearchResumed';
  import { IItemSearchResumed } from '@/interfaces/IItemSearchResumed';
  import AddItemModal from '../AddItemModal.vue';
  import { IOrderItem } from '@/interfaces/IOrderItem';
  import { IItem } from '@/interfaces/IItem';

  @Component
  export default class NewOrderItemsContainer extends Vue {
    $refs: {
      searchInput: HTMLInputElement;
    };
    @Prop() menuId: number | null;
    uploadsPath = Globals.uploadsPath;
    tabIndex = 0;
    search = null;

    get restaurant() {
      return vueData.restaurant;
    }

    get branch() {
      return vueData.branch;
    }

    get currency() {
      return vueData.currency;
    }

    get restaurantSettings() {
      return vueData.restaurantSettings;
    }

    async created() {}

    get selectedCategory() {
      if (!this.allCategories) return null;

      return this.allCategories[this.tabIndex];
    }

    get allItems() {
      const { itemsCache } = vueData;

      if (!itemsCache?.has(this.menuId ? +this.menuId : null)) {
        vueData.addAlert(
          'Ocurrió un error, por favor actualice la página',
          AlertType.Error
        );
        this.$emit('close');
        return;
      }

      return itemsCache
        .get(this.menuId ? +this.menuId : null)
        .filter((i) => i.visible);
    }

    get allCategories() {
      const { categoriesCache } = vueData;

      if (!categoriesCache?.has(this.menuId ? +this.menuId : null)) {
        vueData.addAlert(
          'Ocurrió un error, por favor actualice la página',
          AlertType.Error
        );
        this.$emit('close');
        return;
      }

      return categoriesCache.get(this.menuId ? +this.menuId : null);
    }

    get currentItems() {
      if (this.search) {
        return this.allItems.filter((i) =>
          i.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else if (this.selectedCategory) {
        return this.allItems.filter(
          (i) => i.category.id === this.selectedCategory.id
        );
      } else {
        return this.allItems;
      }
    }

    openItemModal(item: IItem) {
      this.$modal.show(
        AddItemModal,
        {
          itemId: item.id,
          fullItem: item,
          restaurant: this.restaurant,
          branch: this.branch,
          currency: this.currency,
          restaurantSettings: this.restaurantSettings,
        },
        {
          name: 'add-item',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          //width: 1000,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          transition: 'none',
        },
        {
          add: (newOI: IOrderItem) => {
            this.$emit('add', newOI);
          },
        }
      );
    }

    selectItem(item: IItem) {
      this.openItemModal(item);
    }

    toggleSearch() {
      if (this.search === null) {
        this.search = '';
        Vue.nextTick(() => {
          this.$refs.searchInput?.focus();
        });
        //this.$refs.searchInput?.$refs?.input?.focus();
      } else {
        this.search = null;
      }
    }

    clearSearch() {
      console.log('clearSearch');
      Vue.nextTick(() => {
        this.search = '';
        this.$refs.searchInput?.focus();
      });
    }
  }
</script>

<template>
  <div class="new-order-items-container">
    <div class="items-header">
      <v-btn
        class="search-button"
        icon
        @click="toggleSearch"
        large
        :disabled="!allItems || allItems.length === 0"
      >
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
      <v-text-field
        ref="searchInput"
        v-show="search !== null"
        v-model="search"
        placeholder="Ingrese texto a buscar"
        hide-details
        outlined
        class="search-input"
        @blur="search === '' && (search = null)"
      >
        <template v-slot:append>
          <v-btn class="-tw-mt-1.5" icon @click="clearSearch">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-text-field>
      <v-tabs
        v-show="search === null"
        class="category-tabs"
        v-model="tabIndex"
        background-color="transparent"
        :show-arrows="false"
        @scroll.stop
      >
        <v-tab v-for="(category, index) in allCategories" :key="index">
          {{ category.name }}
        </v-tab>
      </v-tabs>
    </div>
    <div class="items">
      <div
        v-for="item of currentItems"
        :key="item.id"
        class="item"
        @click="selectItem(item)"
      >
        <div class="image-container">
          <v-img
            class="item-image"
            :src="uploadsPath + '/' + item.picture"
            :alt="item.name"
            :lazy-src="uploadsPath + '/' + item.picture"
            aspect-ratio="1"
            cover
          ></v-img>
        </div>
        <div class="item-meta">
          <div class="item-name">{{ item.name }}</div>
          <div class="price">
            {{ currency.symbol }}{{ item.discountedPrice ?? item.price }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .new-order-items-container {
    @apply tw-space-y-2;

    .items-header {
      @apply tw-flex tw-space-x-1 tw-items-center;
    }

    .category-tabs {
      @apply tw-flex-1 tw-min-w-0;

      @media (max-width: 568px) {
        ::v-deep() {
          .v-slide-group__next,
          .v-slide-group__prev {
            display: none !important;
          }
        }
      }
    }

    .items {
      @apply tw-grid tw-grid-cols-2 tw-gap-4 tw-py-4;
      @apply xl:tw-grid-cols-3 2xl:tw-grid-cols-4;

      .item {
        @apply tw-flex tw-flex-col tw-gap-2 tw-rounded-md tw-ring-1 tw-ring-gray-200 tw-bg-gray-50 tw-shadow-sm tw-cursor-pointer tw-overflow-hidden;
        @apply md:tw-flex-row md:tw-items-center;

        .item-image {
          @apply tw-bg-gray-100 tw-w-full tw-h-10;
          @apply md:tw-w-20 md:tw-h-20;
        }

        .item-meta {
          @apply tw-flex tw-flex-col tw-justify-center tw-space-y-0 tw-pl-2 tw-pr-4;
          @apply md:tw-flex-1;

          .price {
            @apply tw-text-sm tw-text-gray-600;
          }
        }
      }
    }
  }
</style>
